// Botón `?` + modal de ayuda de Control MDM (`showTt()` del mock). El texto no es el del mock:
// describe el modelo vigente —sin webhooks— y separa nuestro expediente de la observación del proveedor.
import { useState, type ReactNode } from 'react';
import { Eye, LayoutList, Scale, ShieldCheck, type LucideIcon } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const HELP_BUTTON =
    'mr-3 inline-flex size-5 flex-none items-center justify-center self-center rounded-full border border-border bg-muted/40 text-[11px] font-bold leading-none text-muted-foreground transition-colors hover:bg-accent hover:text-foreground';

const SECTION_TITLE = 'text-[11px] font-bold uppercase tracking-[0.04em] text-foreground';

function HelpSection({
    icon: Icon,
    title,
    children,
}: {
    icon: LucideIcon;
    title: string;
    children: ReactNode;
}) {
    return (
        <section className="flex gap-2.5">
            <span className="mt-0.5 flex size-6 flex-none items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground">
                <Icon className="size-3.5" aria-hidden />
            </span>
            <div className="min-w-0 space-y-1">
                <h3 className={SECTION_TITLE}>{title}</h3>
                <div className="space-y-1.5 text-[11.5px] leading-relaxed text-muted-foreground">
                    {children}
                </div>
            </div>
        </section>
    );
}

export function MdmHelpDialog() {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                className={HELP_BUTTON}
                aria-label="¿Qué es esta pantalla?"
                title="¿Qué es esta pantalla?"
                onClick={() => {
                    setOpen(true);
                }}
            >
                ?
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                {/* El pie queda fijo: el texto es largo y el «Cerrar» no puede irse abajo del corte. */}
                <DialogContent className="max-w-xl grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="text-base">Control MDM</DialogTitle>
                        <DialogDescription className="text-[11.5px]">
                            Cómo leer y cómo operar los equipos financiados que están bajo MDM.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
                        <HelpSection icon={Eye} title="Qué es esta pantalla">
                            <p>
                                Es el centro de operación y diagnóstico del parque de equipos
                                financiados: desde aquí bloqueas, desbloqueas y revisas cada
                                teléfono.{' '}
                                <strong className="font-semibold text-foreground">
                                    Hay dos proveedores MDM, Datacultr y Trustonic
                                </strong>
                                , y cada equipo queda con el que se le enroló. No admiten las mismas
                                operaciones, y ni siquiera todos los equipos de un proveedor admiten
                                lo mismo: lo que ESE teléfono no puede hacer se pinta apagado, con
                                el motivo escrito debajo, nunca escondido. Quién es el proveedor de
                                cada equipo es un detalle nuestro, así que va como columna y como
                                filtro, no como el eje de la pantalla.
                            </p>
                        </HelpSection>

                        <HelpSection icon={Scale} title="El estado que manda es el nuestro">
                            <ul className="list-disc space-y-1 pl-4">
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Estado MDM
                                    </strong>{' '}
                                    es nuestro expediente: lo que SureKeep ordenó y confirmó. Es el
                                    que manda y el que ves en la píldora de la lista.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Estado del proveedor
                                    </strong>{' '}
                                    es solo una observación: lo último que el proveedor reportó de
                                    su lado. Sirve para explicar una diferencia, no para decidir.
                                </li>
                            </ul>
                            <p>
                                Cuando los dos coinciden no hay nada que revisar. Cuando difieren,
                                el expediente sigue siendo la verdad y la diferencia es justo lo que
                                tienes que investigar.
                            </p>
                        </HelpSection>

                        <HelpSection icon={LayoutList} title="Cómo está organizada la pantalla">
                            <ul className="list-disc space-y-1 pl-4">
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Barra de KPIs
                                    </strong>
                                    , arriba: la foto de la flota entera, contada sobre las mismas
                                    filas que ves abajo.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Lista de equipos
                                    </strong>
                                    : una sola, con toda la flota. Filtros por IMEI o crédito,
                                    estado MDM, proveedor, señal y marca. Cada orden se manda sobre
                                    UN equipo, desde su ficha.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Ficha del equipo
                                    </strong>
                                    : Panel de control —qué le pasa al equipo, su estado y las
                                    acciones— y Bitácora, que guarda cada llamada al proveedor con
                                    lo que se envió y lo que respondió.
                                </li>
                            </ul>
                            <p>
                                Los dos proveedores comparten una regla:{' '}
                                <strong className="font-semibold text-foreground">
                                    el estado se consulta, no llega solo
                                </strong>
                                . Ninguno avisa por su cuenta cuando una operación termina. En
                                Datacultr la consulta ni siquiera responde el estado del equipo:
                                devuelve la última acción que aplicó y su historial.
                            </p>
                        </HelpSection>

                        <HelpSection icon={ShieldCheck} title="Cómo operar con seguridad">
                            <ul className="list-disc space-y-1 pl-4">
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Las acciones se agrupan por la situación del cliente
                                    </strong>{' '}
                                    —pagó, no paga, no puede usar su equipo, terminó el crédito—, no
                                    por lo que le pasa al equipo.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Acción sugerida
                                    </strong>
                                    : el panel resalta arriba la operación más probable y dice por
                                    qué. Cuando no hay una razón que la sostenga —un equipo activo,
                                    del que esta pantalla no sabe si está al corriente— no sugiere
                                    nada y también lo dice. Un bloqueo por fraude no sugiere nada:
                                    ese desbloqueo necesita autorización.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Una orden, un equipo
                                    </strong>
                                    . No hay acciones en lote: cada operación se lanza desde la
                                    ficha del equipo al que apunta.{' '}
                                    <strong className="font-semibold text-foreground">
                                        El expediente admite una sola orden a la vez
                                    </strong>
                                    , así que mientras haya una en curso el resto queda apagado y
                                    solo quedan las consultas.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Un bloqueo no es inmediato
                                    </strong>
                                    : se programa a futuro. Mientras el corte no cae, la píldora
                                    dice «Bloqueo programado» con la hora a la que entra — el
                                    cliente todavía está usando su teléfono.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Desbloquear NO es Liberar del MDM
                                    </strong>
                                    . <em>Desbloquear</em> le quita el candado al equipo y lo deja
                                    funcionando: sigue administrado y se puede volver a bloquear.{' '}
                                    <em>Liberar del MDM</em> lo saca del MDM para siempre —el equipo
                                    deja de estar administrado y ya no se puede bloquear,
                                    desbloquear ni consultar— y por eso pide teclear el IMEI
                                    completo antes de ejecutarse. En Trustonic confirma en el acto;
                                    en Datacultr queda pendiente y se confirma después.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        Notificar
                                    </strong>{' '}
                                    manda un aviso a la pantalla del equipo y{' '}
                                    <strong className="font-semibold text-foreground">
                                        no cambia su estado
                                    </strong>
                                    : no bloquea ni desbloquea nada. Eliges el aviso del catálogo y
                                    su texto lo redacta el conector de MDM: no se edita desde aquí.
                                </li>
                                <li>
                                    <strong className="font-semibold text-foreground">
                                        PIN de desbloqueo
                                    </strong>
                                    : abre el equipo un rato cuando el cliente no tiene señal.{' '}
                                    <strong className="font-semibold text-foreground">
                                        NO levanta el candado
                                    </strong>
                                    : el bloqueo sigue vigente del lado del proveedor y el equipo se
                                    vuelve a bloquear solo. Para pedirlo necesitas el código que
                                    muestra la pantalla del equipo. Solo lo entrega Trustonic; es un
                                    dato sensible, se muestra una sola vez y no queda en la
                                    bitácora.
                                </li>
                            </ul>
                        </HelpSection>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
